import React, { useState } from 'react';
import { Crown, Heart, Shield, Stethoscope, Award, MapPin } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import BookingModal from '@/components/BookingModal';

const AboutPage = () => {
  const [showBooking, setShowBooking] = useState(false);

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 px-4" data-testid="about-hero">
        <div className="max-w-7xl mx-auto text-center">
          <div className="mb-6 flex justify-center">
            <Crown className="w-16 h-16 text-yellow-400 pulse-gold" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="gradient-text">About EliteIV Lounge</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Royal treatment for your wellness - delivered wherever you are
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 px-4" data-testid="our-story">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-yellow-400/10 to-transparent border border-yellow-500/20 rounded-3xl p-8 md:p-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              <span className="gradient-text">Our Mission</span>
            </h2>
            <div className="space-y-6 text-gray-300 text-lg leading-relaxed">
              <p>
                At EliteIV Lounge, we believe that when you're not feeling your best, you shouldn't have to go anywhere. That's why we've built our entire service around one simple principle: <span className="text-yellow-400 font-semibold">We come to you.</span>
              </p>
              <p>
                Whether you're recovering from illness, seeking performance enhancement, looking for beauty treatments, or simply optimizing your wellness, our mobile IV therapy brings premium care directly to your home, office, or hotel.
              </p>
              <p>
                As a <span className="text-yellow-400 font-semibold">Registered Nurse</span>, I'm passionate about more than just administering treatments - I love educating my patients. Understanding the science behind IV therapy, especially the dramatic difference between oral supplements and intravenous or intramuscular administration, empowers you to make informed decisions about your health.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 px-4 bg-gray-900/50" data-testid="why-choose">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12">
            <span className="gradient-text">Why Choose EliteIV?</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-yellow-400/10 to-transparent border border-yellow-500/20 rounded-2xl p-6">
              <div className="bg-yellow-400/20 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                <Stethoscope className="w-6 h-6 text-yellow-400" />
              </div>
              <h3 className="text-xl font-bold text-yellow-400 mb-3">Licensed RN</h3>
              <p className="text-gray-300">
                All treatments administered by a licensed Registered Nurse with professional medical expertise and patient care experience.
              </p>
            </div>

            <div className="bg-gradient-to-br from-yellow-400/10 to-transparent border border-yellow-500/20 rounded-2xl p-6">
              <div className="bg-yellow-400/20 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                <MapPin className="w-6 h-6 text-yellow-400" />
              </div>
              <h3 className="text-xl font-bold text-yellow-400 mb-3">Mobile Convenience</h3>
              <p className="text-gray-300">
                No need to leave your home or office. We bring everything needed for your treatment directly to your location.
              </p>
            </div>

            <div className="bg-gradient-to-br from-yellow-400/10 to-transparent border border-yellow-500/20 rounded-2xl p-6">
              <div className="bg-yellow-400/20 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                <Heart className="w-6 h-6 text-yellow-400" />
              </div>
              <h3 className="text-xl font-bold text-yellow-400 mb-3">Patient Education</h3>
              <p className="text-gray-300">
                We take time to educate you about your treatment, nutrients, and the science behind IV therapy.
              </p>
            </div>

            <div className="bg-gradient-to-br from-yellow-400/10 to-transparent border border-yellow-500/20 rounded-2xl p-6">
              <div className="bg-yellow-400/20 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-yellow-400" />
              </div>
              <h3 className="text-xl font-bold text-yellow-400 mb-3">Pharmaceutical Grade</h3>
              <p className="text-gray-300">
                Only the highest quality, pharmaceutical-grade vitamins and minerals for optimal results and safety.
              </p>
            </div>

            <div className="bg-gradient-to-br from-yellow-400/10 to-transparent border border-yellow-500/20 rounded-2xl p-6">
              <div className="bg-yellow-400/20 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                <Award className="w-6 h-6 text-yellow-400" />
              </div>
              <h3 className="text-xl font-bold text-yellow-400 mb-3">Personalized Care</h3>
              <p className="text-gray-300">
                Customized treatment plans tailored to your specific wellness goals and health needs.
              </p>
            </div>

            <div className="bg-gradient-to-br from-yellow-400/10 to-transparent border border-yellow-500/20 rounded-2xl p-6">
              <div className="bg-yellow-400/20 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                <Crown className="w-6 h-6 text-yellow-400" />
              </div>
              <h3 className="text-xl font-bold text-yellow-400 mb-3">Elite Experience</h3>
              <p className="text-gray-300">
                Premium service from booking to treatment completion. Your wellness is our priority.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Oral vs IV Education */}
      <section className="py-16 px-4" data-testid="education-section">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12">
            <span className="gradient-text">Why IV Therapy?</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gray-800/50 border border-red-500/20 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-red-400 mb-6">Oral Supplements</h3>
              <ul className="space-y-4 text-gray-300">
                <li className="flex items-start">
                  <span className="text-red-400 mr-2">✗</span>
                  <span>Only 20-30% absorption through digestive system</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-400 mr-2">✗</span>
                  <span>Can cause stomach upset and discomfort</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-400 mr-2">✗</span>
                  <span>Takes hours to feel effects</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-400 mr-2">✗</span>
                  <span>Nutrients degraded by stomach acid</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-400 mr-2">✗</span>
                  <span>Inconsistent results</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-yellow-400/10 to-yellow-600/10 border border-yellow-500/30 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-yellow-400 mb-6">IV/IM Therapy</h3>
              <ul className="space-y-4 text-gray-300">
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">✓</span>
                  <span><strong>100% absorption</strong> directly into bloodstream</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">✓</span>
                  <span>No digestive discomfort</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">✓</span>
                  <span>Feel effects in 30-45 minutes</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">✓</span>
                  <span>Full potency of nutrients delivered</span>
                </li>
                <li className="flex items-start">
                  <span className="text-yellow-400 mr-2">✓</span>
                  <span>Consistent, reliable results</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 px-4 bg-gray-900/50" data-testid="service-areas">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-8">
            <span className="gradient-text">We Serve</span>
          </h2>
          <div className="bg-gradient-to-br from-yellow-400/10 to-transparent border border-yellow-500/20 rounded-2xl p-8">
            <MapPin className="w-12 h-12 text-yellow-400 mx-auto mb-6" />
            <p className="text-xl text-gray-300 mb-6">
              Mobile IV Therapy throughout the Tampa Bay area:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-yellow-400 font-medium">
              <div>Pinellas County</div>
              <div>Hillsborough County</div>
              <div>Pasco County</div>
              <div>Greater Tampa Bay Area</div>
            </div>
            <p className="text-sm text-gray-400 mt-6 italic">
              Service in additional counties may be available upon request — please contact us to discuss.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4" data-testid="about-cta">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="gradient-text">Ready to Feel Your Best?</span>
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Experience the EliteIV difference with professional, mobile IV therapy
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setShowBooking(true)}
              className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-8 py-4 rounded-full text-lg font-bold hover:from-yellow-500 hover:to-yellow-700 transition-all"
              data-testid="about-book-now-btn"
            >
              Book Your Session
            </button>
            <a
              href="tel:727-503-2572"
              className="border-2 border-yellow-400 text-yellow-400 px-8 py-4 rounded-full text-lg font-bold hover:bg-yellow-400 hover:text-black transition-all"
              data-testid="about-call-btn"
            >
              Call 727-503-2572
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <BookingModal isOpen={showBooking} onClose={() => setShowBooking(false)} />
    </div>
  );
};

export default AboutPage;